/**
 * FITS primary-HDU image decoder (BITPIX 8, 16, 32, -32, -64; NAXIS 2 or 3
 * with 1 or 3 planes), as written by scientific and astronomical cameras.
 */
import type { RawFrame } from '../../core/types';
import { defaultHint, makeFrame } from '../frame';

export function decodeFits(bytes: Uint8Array, name: string): RawFrame {
  const cards: Record<string, string> = {};
  let o = 0;
  for (;;) {
    if (o + 80 > bytes.length) throw new Error('FITS header not terminated');
    const card = new TextDecoder('latin1').decode(bytes.subarray(o, o + 80));
    o += 80;
    const key = card.slice(0, 8).trim();
    if (key === 'END') break;
    if (card[8] === '=')
      cards[key] = card
        .slice(10)
        .split('/')[0]
        .trim()
        .replace(/^'(.*)'$/, '$1')
        .trim();
  }
  o = Math.ceil(o / 2880) * 2880;
  const bitpix = parseInt(cards.BITPIX, 10);
  const naxis = parseInt(cards.NAXIS, 10);
  const width = parseInt(cards.NAXIS1, 10);
  const height = parseInt(cards.NAXIS2, 10);
  const planes = naxis >= 3 ? parseInt(cards.NAXIS3, 10) : 1;
  if (naxis < 2) throw new Error('FITS file contains no 2-D image');
  if (planes !== 1 && planes !== 3) throw new Error(`FITS cube with ${planes} planes is not supported`);
  const bzero = cards.BZERO ? parseFloat(cards.BZERO) : 0;
  const bscale = cards.BSCALE ? parseFloat(cards.BSCALE) : 1;
  const bpp = Math.abs(bitpix) / 8;
  const n = width * height;
  if (o + n * planes * bpp > bytes.length) throw new Error('FITS data is truncated');
  const dv = new DataView(bytes.buffer, bytes.byteOffset + o);
  const read = (i: number): number => {
    const p = i * bpp;
    switch (bitpix) {
      case 8:
        return dv.getUint8(p);
      case 16:
        return dv.getInt16(p, false);
      case 32:
        return dv.getInt32(p, false);
      case -32:
        return dv.getFloat32(p, false);
      case -64:
        return dv.getFloat64(p, false);
      default:
        throw new Error(`Unsupported BITPIX ${bitpix}`);
    }
  };
  const channels = planes as 1 | 3;
  // Unsigned 16-bit convention: BITPIX 16 with BZERO 32768.
  const u16 = bitpix === 16 && bzero === 32768 && bscale === 1;
  const u8 = bitpix === 8 && bzero === 0 && bscale === 1;
  let data: Uint8Array | Uint16Array | Float32Array;
  if (u16) data = new Uint16Array(n * channels);
  else if (u8) data = new Uint8Array(n * channels);
  else data = new Float32Array(n * channels);
  // FITS stores rows bottom-to-top (first row = lowest y) and planes one after another.
  for (let c = 0; c < channels; c++)
    for (let y = 0; y < height; y++)
      for (let x = 0; x < width; x++) {
        const v = read(c * n + y * width + x) * bscale + bzero;
        data[((height - 1 - y) * width + x) * channels + c] = v;
      }
  const isFloat = !(u16 || u8);
  const info: Record<string, string> = { BITPIX: `${bitpix}` };
  for (const k of ['INSTRUME', 'TELESCOP', 'EXPTIME', 'DATE-OBS', 'BUNIT']) if (cards[k]) info[k] = cards[k];
  return makeFrame({
    width,
    height,
    data,
    channels,
    maxCode: u16 ? 65535 : 255,
    isFloat,
    bitDepth: Math.abs(bitpix),
    format: 'fits',
    name,
    encoding: { ...defaultHint('FITS data is linear by convention', 'linear'), source: 'format' },
    info,
  });
}
